import { usePage } from '@inertiajs/react'
import type { PageProps, Flash } from '@/types'
import { useEffect, useState, useCallback, useRef } from 'react'
import Sidebar from './Sidebar'
import CommandPalette from '@/components/search/CommandPalette'

const SIDEBAR_KEY = 'sidebar-width'
const DEFAULT_WIDTH = 240
const MIN_WIDTH = 180
const MAX_WIDTH = 480

function FlashToast({ flash }: { flash: Flash }) {
  const [visible, setVisible] = useState(false)
  const message = flash.success || flash.error || flash.alert

  useEffect(() => {
    if (message) {
      setVisible(true)
      const timer = setTimeout(() => setVisible(false), 4000)
      return () => clearTimeout(timer)
    }
  }, [message])

  if (!visible || !message) return null

  const bg = flash.error
    ? 'bg-red-50 border-red-200 text-red-800'
    : flash.alert
      ? 'bg-yellow-50 border-yellow-200 text-yellow-800'
      : 'bg-green-50 border-green-200 text-green-800'

  return (
    <div className={`fixed top-4 right-4 z-50 rounded-md border px-4 py-3 text-sm ${bg}`}>
      {message}
    </div>
  )
}

export default function Layout({ children, fullWidth = false }: { children: React.ReactNode; fullWidth?: boolean }) {
  const { flash } = usePage<PageProps>().props
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_KEY)
    return saved ? Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, Number(saved))) : DEFAULT_WIDTH
  })
  const isResizing = useRef(false)

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    isResizing.current = true
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'

    const onMouseMove = (e: MouseEvent) => {
      if (!isResizing.current) return
      const newWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, e.clientX))
      setSidebarWidth(newWidth)
    }

    const onMouseUp = () => {
      isResizing.current = false
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
      localStorage.setItem(SIDEBAR_KEY, String(Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, parseInt(document.querySelector<HTMLElement>('[data-sidebar]')?.style.width || String(DEFAULT_WIDTH))))))
    }

    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
  }, [])

  useEffect(() => {
    localStorage.setItem(SIDEBAR_KEY, String(sidebarWidth))
  }, [sidebarWidth])

  return (
    <div className="flex h-screen bg-white text-gray-900">
      <Sidebar width={sidebarWidth} />

      <div
        onMouseDown={handleMouseDown}
        className="w-1 cursor-col-resize hover:bg-gray-300 active:bg-gray-400 transition-colors flex-shrink-0"
      />

      <main className={`flex-1 min-w-0 ${fullWidth ? 'overflow-hidden' : 'overflow-y-auto'}`}>
        <FlashToast flash={flash} />
        {fullWidth ? children : (
          <div className="mx-auto max-w-5xl p-8">
            {children}
          </div>
        )}
      </main>

      <CommandPalette />
    </div>
  )
}
